import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, KeyRound } from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { Callout, EmptyState } from '../../shared/components/ui/primitives';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { EmailLeadSettingsPanel } from '../components/forms/EmailLeadSettingsModal';
import PortalLeadsTable from '../components/email/PortalLeadsTable';
import CompanySenders from '../components/email/CompanySenders';
import PersonalSender from '../components/email/PersonalSender';
import EmailLogo from '../components/email/EmailLogo';
import useEmailLeadSettings from '../hooks/useEmailLeadSettings';
import useEmailSenders from '../hooks/useEmailSenders';
import { emailHubTab, receivingSummary } from '../utils/emailSendersAccess';

/**
 * Integrations → Email & Inboxes.
 *
 * One place for everything that sends or receives email, because the Gmail /
 * Zoho app password behind both is the same credential:
 *
 *  - Lead Ingestion: the inboxes 99acres, Housing.com and NoBroker alerts
 *    arrive in (what was the Portal Lead Emails modal), the routing
 *    suggestions, and every email read from them.
 *  - Outbound Email: one sender per company (what was Email Setup → View
 *    Companies) and the user's own Gmail (also on the Profile page).
 *
 * Only the screens moved. Each part still talks to its own endpoints and keeps
 * its own permissions — connecting an inbox is still Builder/Management only.
 */

const TABS = [
  { value: 'leads', label: 'Lead Ingestion' },
  { value: 'outbound', label: 'Outbound Email' }
];

function errorText(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function EmailLeads() {
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = emailHubTab(searchParams.get('tab'));
  const leadsRef = useRef(null);
  const userId = Cookies.get('userId');

  const {
    mailboxes,
    connected,
    isPending: settingsPending,
    isError: settingsFailed,
    error: settingsError
  } = useEmailLeadSettings();

  const senders = useEmailSenders();

  /**
   * Only a 403 means "your role cannot see this".
   *
   * Anything else — a 500 from a missing migration, a gateway error — is a
   * server problem, and telling someone to ask their account owner for access
   * sends them to the wrong place entirely.
   */
  const accessDenied = settingsError?.response?.status === 403;

  useEffect(() => {
    mixpanel.track(tab === 'outbound' ? 'Mail Companies Viewed' : 'Email Leads Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [tab, userId]);

  // "View Leads" on the Integrations card lands here with ?view=leads.
  const wantsLeads = searchParams.get('view') === 'leads';
  useEffect(() => {
    if (tab === 'leads' && wantsLeads && connected && leadsRef.current) {
      leadsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [tab, wantsLeads, connected]);

  const selectTab = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  const receiving = settingsPending
    ? 'Checking…'
    : settingsFailed
      ? 'Unavailable'
      : receivingSummary(mailboxes);

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Email & Inboxes' }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] min-h-screen w-full space-y-6 bg-gray-100 p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-center gap-4">
              <EmailLogo className="h-12 w-12 shrink-0" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Email &amp; Inboxes</h1>
                <p className="text-sm text-gray-600">
                  Sending and portal lead inboxes, in one place.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="min-w-[10rem] rounded-panel border border-hairline bg-surface px-4 py-2 shadow-card">
                <p className="text-xs text-gray-500">Sending</p>
                <p className="text-sm font-semibold text-gray-900">
                  {senders.isPending ? 'Checking…' : senders.summary}
                </p>
              </div>
              <div className="min-w-[10rem] rounded-panel border border-hairline bg-surface px-4 py-2 shadow-card">
                <p className="text-xs text-gray-500">Receiving</p>
                <p className="text-sm font-semibold text-gray-900">{receiving}</p>
              </div>
            </div>
          </div>

          <div
            role="tablist"
            aria-label="Email sections"
            className="flex gap-6 border-b border-hairline-strong">
            {TABS.map((entry) => {
              const selected = tab === entry.value;
              return (
                <button
                  key={entry.value}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => selectTab(entry.value)}
                  className={`-mb-px border-b-[3px] px-1 pb-3 pt-1 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                    selected
                      ? 'border-brand-700 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}>
                  {entry.label}
                </button>
              );
            })}
          </div>

          {tab === 'outbound' ? (
            <div className="space-y-8">
              <Callout tone="brand" icon={<KeyRound className="h-4 w-4" aria-hidden="true" />}>
                A sender whose address also receives portal alerts can reuse its app password on the
                Lead Ingestion tab.
              </Callout>
              <CompanySenders
                companySenders={senders.companySenders}
                isPending={senders.isPending}
              />
              <PersonalSender personal={senders.personal} isPending={senders.personalPending} />
            </div>
          ) : settingsFailed && accessDenied ? (
            <EmptyState
              title="You do not have access to portal lead emails."
              hint="Ask your account owner for access."
            />
          ) : settingsFailed ? (
            <Callout
              tone="danger"
              icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
              title="Could not load your inbox settings">
              {errorText(settingsError, 'The server did not respond.')} This is a server error, not
              a permission problem.
            </Callout>
          ) : (
            <div className="space-y-8">
              <EmailLeadSettingsPanel senderAddresses={senders.senderAddresses} showIntro={false} />
              {connected ? (
                <div ref={leadsRef} className="scroll-mt-24">
                  <PortalLeadsTable enabled={connected} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
